import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./font.css";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { ThemeProvider } from "@mui/material";
import Preferences from "./components/account/Preferences";
import FourOhFour from "./components/fourohfour";
import Shopping from "./components/account/Shopping";
import Payouts from "./components/account/Payouts";
import InfoPage from "./components/infoPage";
import Login from "./components/Login";
import Account from "./components/account/Account";
import { ContextProvider, Context } from "./common/context";
import { createCustomTheme } from "./components/account/theme";
import Register from "./components/Register";
import GetCode from "./components/GetCode";
import ErrorPage from "./components/ErrorBoundaryPage";
import { baseApi } from "./common/constants";
import { Axios } from "./common/utils";
import Layout from "./components/layout";
import CanonicalUrl from "./common/CanonicalUrl";
import type { ContextValue } from "./types";

const logError = (error: Error, info: React.ErrorInfo, value: ContextValue) => {
    if (!value?.context || value.context?.adminConfig?.mall?.log_errors) {
        return Axios.put(`${baseApi}reporterror`, {
            error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
            info: JSON.stringify(info),
        });
    }
};

const root = createRoot(document.getElementById(`root`) as HTMLElement);
root.render(
    <React.StrictMode>
        <Router>
            <CanonicalUrl />
            <ContextProvider>
                <Context.Consumer>
                    {(value: ContextValue) => (
                        <ThemeProvider theme={createCustomTheme({ context: value.context })}>
                            <ErrorBoundary
                                FallbackComponent={ErrorPage}
                                onError={(e, i) => logError(e as Error, i as React.ErrorInfo, value)}
                            >
                                <Routes>
                                    <Route path="/" element={<Layout {...value} />}>
                                        <Route path="i/:page" element={<InfoPage {...value} />} />
                                        <Route index element={<Account {...value} />} />
                                        <Route
                                            path="shopping/:year?/:status?/:search?"
                                            element={<Shopping {...value} />}
                                        />
                                        <Route path="payouts" element={<Payouts {...value} />} />
                                        <Route path="preferences" element={<Preferences {...value} />} />
                                    </Route>
                                    <Route path="*" element={<FourOhFour />} />
                                    <Route path="login" element={<Login {...value} mode="login" />} />
                                    <Route path="signup" element={<Login {...value} mode="signup" />} />
                                    <Route path="getcode" element={<GetCode {...value} />} />
                                    <Route path="register" element={<Register {...value} />} />
                                </Routes>
                            </ErrorBoundary>
                        </ThemeProvider>
                    )}
                </Context.Consumer>
            </ContextProvider>
        </Router>
    </React.StrictMode>
);
